import {fabric} from "fabric";
import {convertPixelToCm, convertPixelToFeet} from "./convertToPixel";

const ALIGNING_LINE_COLOR = "#FF1EFF"
const ALIGNING_LINE_WIDTH = 0.5
const ALIGNING_LINE_DASH = false
const DISTANCE_TOLERANCE = 4
const NUMBER_FONT_SIZE = 11

function drawHoverGuidesForTextFrame(ctx, target, oCoords, canvas) {
    if (target.type === "image") {
        if (target.group) return

        ctx.save()
        ctx.transform(...canvas.viewportTransform)
        const center = target.getPointByOrigin("left", "top")

        ctx.translate(center.x, center.y)
        ctx.rotate(degreesToRadians(target.angle))
        ctx.translate(-center.x, -center.y)

        ctx.scale(target.scaleX, target.scaleY)

        ctx.beginPath()
        ctx.lineWidth = 1.3
        ctx.strokeStyle = canvas.hoverStrokeColor

        ctx.rect(target.left, target.top, target.width, target.height)

        ctx.scale(1 / target.scaleX, 1 / target.scaleY)

        if (target.group)
            ctx.scale(1 / target.group.scaleX, 1 / target.group.scaleY)
        ctx.stroke()
        ctx.restore()
    }

    else if (target.type === "text" && target.path) {
        ctx.save()
        ctx.transform(...canvas.viewportTransform)

        scaleAllGroups(target, ctx)

        ctx.translate(target.left, target.top)
        ctx.rotate(degreesToRadians(target.angle))
        const scaleX = target.flipX ? -target.scaleX : target.scaleX
        const scaleY = target.flipY ? -target.scaleY : target.scaleY
        ctx.scale(scaleX, scaleY)
        ctx.beginPath()
        ctx.lineWidth = 1.3
        ctx.strokeStyle = canvas.hoverStrokeColor

        target.path._renderPathCommands(ctx)

        ctx.scale(1 / scaleX, 1 / scaleY)

        deScaleAllGroups(target, ctx)

        ctx.stroke()
        ctx.restore()

        return
    }
}

function degreesToRadians(degrees) {
    return degrees * (Math.PI / 180);
}

function deScaleAllGroups(target, ctx) {
    if (target.group) {
        ctx.scale(1 / target.group.scaleX, 1 / target.group.scaleY)

        deScaleAllGroups(target.group, ctx)
    }
}

function scaleAllGroups(target, ctx) {
    if (target.group) {
        ctx.translate(target.group.left, target.group.top)
        ctx.rotate(degreesToRadians(target.group.angle))
        ctx.scale(target.group.scaleX, target.group.scaleY)

        scaleAllGroups(target.group, ctx)
    }
}

function drawNumberBox(
    ctx,
    r,
    x,
    y,
    text,
    fontSize = NUMBER_FONT_SIZE,
    zoom = 1,
    bgColor = ALIGNING_LINE_COLOR,
    textColor = "white",
    minWidth
) {
    ctx.beginPath()
    ctx.fillStyle = bgColor
    const width = (minWidth || ctx.measureText(text).width) / zoom
    const widthOffset = 11 / zoom
    const heightOffset = (fontSize - 4) / zoom
    const rectX = x - width / 2 - widthOffset
    const rectY = y - heightOffset
    const rectW = width + widthOffset * 2
    const rectH = heightOffset * 2

    r = 8 / zoom
    ctx.moveTo(rectX + r, rectY)
    ctx.arcTo(rectX + rectW, rectY, rectX + rectW, rectY + rectH, r)
    ctx.arcTo(rectX + rectW, rectY + rectH, rectX, rectY + rectH, r)
    ctx.arcTo(rectX, rectY + rectH, rectX, rectY, r)
    ctx.arcTo(rectX, rectY, rectX + rectW, rectY, r)
    ctx.fill()

    ctx.beginPath()
    ctx.fillStyle = textColor
    ctx.font = `600 ${fontSize / zoom}px Inter, sans-serif`
    ctx.textAlign = "center"

    // This is just a guess, varies font to font
    const lineHeightOffset = 4 / zoom

    ctx.fillText(text, x, y + lineHeightOffset)
}

function drawDualNumbersBox(
    ctx,
    r,
    x,
    y,
    numbers,
    fontSize = NUMBER_FONT_SIZE,
    zoom = 1,
    bgColor = ALIGNING_LINE_COLOR,
    textColor = "white",
    minWidth,
    height
) {
    ctx.beginPath()
    ctx.fillStyle = bgColor
    const width = minWidth / zoom
    const widthOffset = 11 / zoom
    const heightOffset = height / zoom
    const rectX = x - width / 2 - widthOffset
    const rectY = y - heightOffset
    const rectW = width
    const rectH = heightOffset

    r = 10 / zoom
    ctx.moveTo(rectX + r, rectY)
    ctx.arcTo(rectX + rectW, rectY, rectX + rectW, rectY + rectH, r)
    ctx.arcTo(rectX + rectW, rectY + rectH, rectX, rectY + rectH, r)
    ctx.arcTo(rectX, rectY + rectH, rectX, rectY, r)
    ctx.arcTo(rectX, rectY, rectX + rectW, rectY, r)
    ctx.fill()

    ctx.beginPath()
    ctx.fillStyle = textColor
    ctx.font = `600 ${fontSize / zoom}px Inter, sans-serif`
    ctx.textAlign = "left"

    // This is just a guess, varies font to font
    const lineHeightOffset = 5 / zoom
    const offset = 8 / zoom

    const labelWidths = []
    numbers.forEach((number) => {
        labelWidths.push(ctx.measureText(number.label).width)
    })
    const maxLabelWidth = Math.max(...labelWidths) + 4 / zoom

    numbers.forEach((number, index) => {
        ctx.globalAlpha = 0.5
        ctx.fillText(
            number.label,
            rectX + offset /*+ lineHeightOffset*/,
            rectY +
            (lineHeightOffset + offset) * (index + 1) /*+ (lineHeightOffset/2)*/
        )
        ctx.globalAlpha = 1
        ctx.fillText(
            " " + number.value,
            rectX + offset /*+ lineHeightOffset*/ + maxLabelWidth,
            rectY +
            (lineHeightOffset + offset) * (index + 1) /*+ (lineHeightOffset/2)*/
        )
    })
}

/**
 * Creates a grid lines group for the canvas
 *
 * @param {fabric.Canvas} canvas
 * @param {number} zoom
 */
function createPixelGridLines(canvas, zoom = 1) {
    if (!canvas.drawPixelGrids) return

    const ctx = canvas.getSelectionContext()
    if (!ctx) return

    ctx.save()
    ctx.transform(...canvas.viewportTransform)

    const bounds = canvas.getViewportTransformBounds()
    const vCellsCount = bounds.height
    const hCellsCount = bounds.width

    ctx.beginPath()
    ctx.lineWidth = 1 / zoom
    ctx.strokeStyle = "rgba(0, 0, 0, 0.07)"

    for (let i = 0; i <= hCellsCount; i++) {
        var offsetXPos = Math.ceil(bounds.left)
        var xPos = offsetXPos + i
        var topPoint = new fabric.Point(xPos, bounds.top)
        var bottomPoint = new fabric.Point(xPos, bounds.bottom)

        ctx.moveTo(topPoint.x, topPoint.y)
        ctx.lineTo(bottomPoint.x, bottomPoint.y)
    }

    for (let i = 0; i <= vCellsCount; i++) {
        var offsetYPos = Math.ceil(bounds.top)
        var yPos = offsetYPos + i
        var leftPoint = new fabric.Point(bounds.left, yPos)
        var rightPoint = new fabric.Point(bounds.right, yPos)

        ctx.moveTo(leftPoint.x, leftPoint.y)
        ctx.lineTo(rightPoint.x, rightPoint.y)
    }

    ctx.stroke()
    ctx.restore()
}

function resetObjectAngle(object) {
    object.angle = object.angle % 360 || 0
    if (object.angle < 0) object.angle += 360

    return object.angle
}

/**
 * @description Finds all group objects inside a given group
 *
 * @param {fabric.Group} parent
 * @param {[]} objects - best left empty
 *
 * @returns {fabric.Object[]}
 */
function deepFindGroupedObjects(group, objects = []) {
    group._objects.forEach((obj) => {
        if (obj.type !== "group") {
            objects.push(obj)
        } else {
            deepFindGroupedObjects(obj, objects)
        }
    })

    return objects
}

function findTopMostGroup(object) {
    var group = object.group

    while (group?.group) {
        group = group.group
    }

    return group
}

/**
 * @description draws alignment guide lines on the selection canvas context
 *
 * @param {fabric.Canvas} [canvas] fabric canvas instance
 * @returns void
 */
export function initAligningGuidelines(canvas) {
    const ctx = canvas.getSelectionContext()
    var viewportTransform

    function drawVerticalDistanceLines(info) {
        ctx.save()
        ctx.transform(...viewportTransform)
        const zoom = canvas.getZoom()

        ctx.beginPath()
        ctx.lineWidth = ALIGNING_LINE_WIDTH / zoom
        ctx.strokeStyle = ALIGNING_LINE_COLOR
        ctx.moveTo(info.x, info.y1)
        ctx.lineTo(info.x, info.y2)

        info.bounds.forEach(({ left, top, width, height }) => {
            ctx.rect(left, top, width, height)
        })

        ctx.stroke()
        ctx.restore()

        return ctx
    }

    function drawVerticalDistance(info) {
        const distance = Number(Math.abs(info.y1 - info.y2).toFixed(2))
        if (distance === 0) return

        const zoom = canvas.getZoom()
        ctx.save()
        ctx.transform(...viewportTransform)

        if (info.connector) {
            ctx.beginPath()
            ctx.lineWidth = ALIGNING_LINE_WIDTH / zoom
            ctx.strokeStyle = ALIGNING_LINE_COLOR
            ctx.setLineDash([5 / zoom])
            ctx.moveTo(info.connector.x1, info.connector.y1)
            ctx.lineTo(info.connector.x2, info.connector.y2)
            ctx.stroke()
        }

        drawNumberBox(
            ctx,
            3,
            info.x,
            (info.y2 + info.y1) / 2,
            distance,
            NUMBER_FONT_SIZE,
            zoom
        )

        ctx.restore()

        return ctx
    }

    function drawHorizontalDistanceLines(info) {
        ctx.save()
        ctx.transform(...viewportTransform)
        const zoom = canvas.getZoom()

        ctx.beginPath()
        ctx.lineWidth = ALIGNING_LINE_WIDTH / zoom
        ctx.strokeStyle = ALIGNING_LINE_COLOR
        ctx.moveTo(info.x1, info.y)
        ctx.lineTo(info.x2, info.y)

        info.bounds.forEach(({ left, top, width, height }) => {
            ctx.rect(left, top, width, height)
        })
        ctx.stroke()

        ctx.restore()

        return ctx
    }

    function drawHorizontalDistance(info) {
        const distance = Number(Math.abs(info.x1 - info.x2).toFixed(2))
        if (distance === 0) return

        const zoom = canvas.getZoom()
        ctx.save()
        ctx.transform(...viewportTransform)

        if (info.connector) {
            ctx.beginPath()
            ctx.lineWidth = ALIGNING_LINE_WIDTH / zoom
            ctx.strokeStyle = ALIGNING_LINE_COLOR
            ctx.setLineDash([5 / zoom])
            ctx.moveTo(info.connector.x1, info.connector.y1)
            ctx.lineTo(info.connector.x2, info.connector.y2)
            ctx.stroke()
        }

        drawNumberBox(
            ctx,
            3,
            (info.x2 + info.x1) / 2,
            info.y,
            distance,
            NUMBER_FONT_SIZE,
            zoom
        )
        ctx.restore()

        return ctx
    }

    function drawVerticalLines(coords) {
        drawLine(
            coords.x,
            coords.y1 > coords.y2 ? coords.y2 : coords.y1,
            coords.x,
            coords.y2 > coords.y1 ? coords.y2 : coords.y1,
            "vertical"
        )
    }

    function drawHorizontalLines(coords) {
        drawLine(
            coords.x1 > coords.x2 ? coords.x2 : coords.x1,
            coords.y,
            coords.x2 > coords.x1 ? coords.x2 : coords.x1,
            coords.y,
            "horizontal"
        )
    }

    /**
     * Draw the guide lines
     *
     * @param {number} x1
     * @param {number} y1
     * @param {number} x2
     * @param {number} y2
     * @param {string} type
     */
    function drawLine(x1, y1, x2, y2, type) {
        ctx.save()

        const zoom = canvas.getZoom()

        ctx.lineWidth = ALIGNING_LINE_WIDTH / zoom
        ALIGNING_LINE_DASH && ctx.setLineDash([5 / zoom])
        ctx.strokeStyle = ALIGNING_LINE_COLOR
        ctx.transform(...viewportTransform)

        if (type === "vertical") {
            ctx.beginPath()
            ctx.moveTo(x1 - 3 / zoom, y1)
            ctx.lineTo(x1 + 3 / zoom, y1)
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(x2 - 3 / zoom, y2)
            ctx.lineTo(x2 + 3 / zoom, y2)
            ctx.stroke()
        } else {
            ctx.beginPath()
            ctx.moveTo(x1, y1 - 3 / zoom)
            ctx.lineTo(x1, y1 + 3 / zoom)
            ctx.stroke()

            ctx.beginPath()
            ctx.moveTo(x2, y2 - 3 / zoom)
            ctx.lineTo(x2, y2 + 3 / zoom)
            ctx.stroke()
        }

        ctx.beginPath()
        ctx.moveTo(x1, y1)
        ctx.lineTo(x2, y2)
        ctx.stroke()

        ctx.restore()
    }

    function drawMovementLockLine(data) {
        const angle =
            Math.PI / 2 +
            fabric.util.getLineAngle(data[2], data[3], data[0], data[1], false, false)
        const zoom = canvas.getZoom()
        ctx.save()
        ctx.transform(...canvas.viewportTransform)
        ctx.beginPath()
        ctx.lineWidth = 0.75 / zoom
        ctx.strokeStyle = ALIGNING_LINE_COLOR

        if (data[0] === data[2] && data[1] === data[3]) {
            ctx.moveTo(data[2] - 4 / zoom, data[3])
            ctx.lineTo(data[2] + 4 / zoom, data[3])
            ctx.moveTo(data[2], data[3] - 4 / zoom)
            ctx.lineTo(data[2], data[3] + 4 / zoom)
            ctx.stroke()

            return ctx.restore()
        }

        ctx.moveTo(data[2], data[3])
        ctx.lineTo(data[0], data[1])

        ctx.save()
        ctx.translate(data[2], data[3])
        ctx.rotate(angle)
        ctx.translate(-data[2], -data[3])
        ctx.moveTo(data[2] - 3 / zoom, data[3])
        ctx.lineTo(data[2] + 3 / zoom, data[3])
        ctx.restore()

        ctx.save()
        ctx.translate(data[0], data[1])
        ctx.rotate(angle)
        ctx.translate(-data[0], -data[1])
        ctx.moveTo(data[0] - 3 / zoom, data[1])
        ctx.lineTo(data[0] + 3 / zoom, data[1])
        ctx.restore()

        ctx.stroke()

        ctx.restore()
    }

    function drawNumberGuide(data) {
        const zoom = canvas.getZoom()
        ctx.save()
        ctx.transform(...canvas.viewportTransform)

        const text = data.number
        drawNumberBox(
            ctx,
            3,
            data.x - (data.offsetX || 0) / zoom,
            data.y - (data.offsetY || 0) / zoom,
            text,
            11,
            zoom,
            fabric.Canvas.prototype.hoverStrokeColor,
            "white",
            data.minWidth
        )
        ctx.restore()
    }

    function drawDualNumbersGuide(data) {
        const zoom = canvas.getZoom()
        ctx.save()
        ctx.transform(...canvas.viewportTransform)

        drawDualNumbersBox(
            ctx,
            3,
            data.x + (data.offsetX || 0) / zoom,
            data.y - (data.offsetY || 0) / zoom,
            data.numbers,
            10,
            zoom,
            fabric.Canvas.prototype.hoverStrokeColor,
            "white",
            data.minWidth,
            data.height
        )
        ctx.restore()
    }

    function isInRange(value1, value2) {
        const zoom = canvas.getZoom()
        value1 = Math.round(value1)
        value2 = Math.round(value2)
        for (
            let i = value1 - Math.round(DISTANCE_TOLERANCE / zoom),
                len = value1 + Math.round(DISTANCE_TOLERANCE / zoom);
            i <= len;
            i++
        ) {
            if (i === value2) return true
        }

        return false
    }

    var verticalLines = []
    var horizontalLines = []
    var horizontalDistance = null
    var verticalDistance = null
    var widthDistance = null
    var heightDistance = null
    var movementLock = null
    var numberGuide = null
    var dualNumbersGuide = null


    document.addEventListener("keyup", ({ code }) => {
        if (canvas.__frameSubTargets) {
            if (
                code === "MetaLeft" ||
                code === "MetaRight" ||
                code === "ControlLeft" ||
                code === "ControlRight"
            ) {
                canvas.fire("mouse:out", {
                    target: canvas.__frameSubTargets,
                })
            }
        }

        if (code === "AltLeft" || code === "AltRight") {
            horizontalDistance = null
            verticalDistance = null
            widthDistance = null
            heightDistance = null
            delete canvas.__lastTarget
            canvas._calculatingDistance = false

            canvas.requestRenderAll()
        }
    })

    /*canvas.on("object:moved", function({
          eventSource,
          e: {
              altKey
          }
      }) {
          if (!altKey || eventSource !== 'keyboard') return;

          measureDistances(canvas.__lastTarget);
      });

      canvas.on("mouse:move", function({
          target,
          e: {
              altKey
          }
      }) {
          if (target) canvas.__lastTarget = target;
          else delete canvas.__lastTarget;

          if (canvas.__clonedFrom) measureDistances(canvas.__clonedFrom);
          else if (altKey) measureDistances(target);
      });*/

    function measureDistances(target) {
        const active = canvas.getActiveObject()
        if (!target || !active) return
        if (active === target) return
        if (!active.isOnScreen() || !target.isOnScreen()) return

        if (
            active.__warpMode ||
            active.__cropMode ||
            active.__perspectiveMode ||
            active.editMode ||
            active.__isDrawing
        )
            return

        viewportTransform = canvas.viewportTransform

        const targetCenter = target.getPointByOrigin("center", "center")
        if (target.group) {
            setGroupedPointByOriginRecursively(
                target.group,
                targetCenter,
                "center",
                "center"
            )
        }

        const targetBounds = target.getBoundingRect(true, !!canvas.__clonedFrom)
        const targetWidth = targetBounds.width
        const targetHeight = targetBounds.height
        const targetLeft = targetCenter.x - targetWidth / 2
        const targetRight = targetCenter.x + targetWidth / 2
        const targetTop = targetCenter.y - targetHeight / 2
        const targetBottom = targetCenter.y + targetHeight / 2

        const targetBoundingRect = {
            width: targetWidth,
            height: targetHeight,
            top: targetTop,
            left: targetLeft,
        }

        const activeCenter = active.getPointByOrigin("center", "center")
        if (active.group) {
            setGroupedPointByOriginRecursively(
                active.group,
                activeCenter,
                "center",
                "center"
            )
        }

        const activeBounds = active.getBoundingRect(true, !!canvas.__clonedFrom)
        const activeWidth = activeBounds.width
        const activeHeight = activeBounds.height
        const activeLeft = activeCenter.x - activeWidth / 2
        const activeRight = activeCenter.x + activeWidth / 2
        const activeTop = activeCenter.y - activeHeight / 2
        const activeBottom = activeCenter.y + activeHeight / 2

        const activeBoundingRect = {
            width: activeWidth,
            height: activeHeight,
            top: activeTop,
            left: activeLeft,
        }

        if (
            (target.type === "frame" &&
                active._wrapperFrame &&
                active._wrapperFrame === target) ||
            active.intersectsWithObject(target)
        ) {
            horizontalDistance = {
                bounds: [targetBoundingRect, activeBoundingRect],
                x1: activeLeft,
                x2: targetLeft,
                y: activeCenter.y,
            }

            verticalDistance = {
                bounds: [targetBoundingRect, activeBoundingRect],
                y1: targetTop,
                y2: activeTop,
                x: activeCenter.x,
            }

            widthDistance = {
                bounds: [targetBoundingRect, activeBoundingRect],
                x1: activeRight,
                x2: targetRight,
                y: activeCenter.y,
            }

            heightDistance = {
                bounds: [targetBoundingRect, activeBoundingRect],
                y1: targetBottom,
                y2: activeBottom,
                x: activeCenter.x,
            }

            if (activeCenter.x <= targetLeft) {
                verticalDistance.x = (activeRight + targetLeft) / 2
                heightDistance.x = (activeRight + targetLeft) / 2
            }

            if (activeCenter.x >= targetRight) {
                verticalDistance.x = (activeLeft + targetRight) / 2
                heightDistance.x = (activeLeft + targetRight) / 2
            }

            if (activeCenter.y >= targetBottom) {
                horizontalDistance.y = (activeTop + targetBottom) / 2
                widthDistance.y = (activeTop + targetBottom) / 2
            }

            if (activeCenter.y <= targetTop) {
                horizontalDistance.y = (activeBottom + targetTop) / 2
                widthDistance.y = (activeBottom + targetTop) / 2
            }

            if (
                (activeTop === targetBottom && activeRight === targetLeft) ||
                (activeTop === targetBottom && activeLeft === targetRight) ||
                (activeBottom === targetTop && activeRight === targetLeft) ||
                (activeBottom === targetTop && activeLeft === targetRight)
            ) {
                horizontalDistance = null
                verticalDistance = null
                widthDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    y1: 0,
                    y2: 0,
                    x: 0,
                }
                heightDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    y1: 0,
                    y2: 0,
                    x: 0,
                }
            }
        }

        if (activeCenter.x > targetCenter.x) {
            if (targetRight < activeLeft) {
                horizontalDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    x1: targetRight,
                    x2: activeLeft,
                    y: activeCenter.y,
                }

                if (activeCenter.y > targetBottom) {
                    if (activeTop > targetBottom) {
                        horizontalDistance.connector = {
                            x1: targetRight,
                            y1: activeCenter.y,
                            x2: targetRight,
                            y2: targetBottom,
                        }
                    } else {
                        horizontalDistance.y = targetBottom
                    }
                }

                if (activeCenter.y < targetTop) {
                    if (activeBottom < targetTop) {
                        horizontalDistance.connector = {
                            x1: targetRight,
                            y1: activeCenter.y,
                            x2: targetRight,
                            y2: targetTop,
                        }
                    } else {
                        horizontalDistance.y = targetTop
                    }
                }
            }
        } else {
            if (activeRight < targetLeft) {
                horizontalDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    x1: activeRight,
                    x2: targetLeft,
                    y: activeCenter.y,
                }

                if (activeCenter.y > targetBottom) {
                    if (activeTop > targetBottom) {
                        horizontalDistance.connector = {
                            x1: targetLeft,
                            y1: activeCenter.y,
                            x2: targetLeft,
                            y2: targetBottom,
                        }
                    } else {
                        horizontalDistance.y = targetBottom
                    }
                }

                if (activeCenter.y < targetTop) {
                    if (activeBottom < targetTop) {
                        horizontalDistance.connector = {
                            x1: targetLeft,
                            y1: activeCenter.y,
                            x2: targetLeft,
                            y2: targetTop,
                        }
                    } else {
                        horizontalDistance.y = targetTop
                    }
                }
            }
        }

        // Vertical distance
        if (activeCenter.y > targetCenter.y) {
            if (targetBottom < activeTop) {
                verticalDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    y1: targetBottom,
                    y2: activeTop,
                    x: activeCenter.x,
                }

                if (activeCenter.x < targetLeft) {
                    if (activeRight < targetLeft) {
                        verticalDistance.connector = {
                            x1: activeCenter.x,
                            y1: targetBottom,
                            x2: targetLeft,
                            y2: targetBottom,
                        }
                    } else {
                        verticalDistance.x = targetLeft
                    }
                }

                if (activeCenter.x > targetRight) {
                    if (activeRight > targetRight) {
                        verticalDistance.connector = {
                            x1: activeCenter.x,
                            y1: targetBottom,
                            x2: targetRight,
                            y2: targetBottom,
                        }
                    } else {
                        verticalDistance.x = targetRight
                    }
                }
            }
        } else {
            if (activeBottom < targetTop) {
                verticalDistance = {
                    bounds: [targetBoundingRect, activeBoundingRect],
                    y1: activeBottom,
                    y2: targetTop,
                    x: activeCenter.x,
                }

                if (activeCenter.x < targetLeft) {
                    if (activeRight < targetLeft) {
                        verticalDistance.connector = {
                            x1: activeCenter.x,
                            y1: targetTop,
                            x2: targetLeft,
                            y2: targetTop,
                        }
                    } else {
                        verticalDistance.x = targetLeft
                    }
                }

                if (activeCenter.x > targetRight) {
                    if (activeLeft > targetRight) {
                        verticalDistance.connector = {
                            x1: activeCenter.x,
                            y1: targetTop,
                            x2: targetRight,
                            y2: targetTop,
                        }
                    } else {
                        verticalDistance.x = targetRight
                    }
                }
            }
        }

        canvas.requestRenderAll()
    }

    function targetIsFrameChild(target) {
        if (target.type !== "activeSelection") {
            return !!target._wrapperFrame
        } else {
            const found = target.getObjects().find((o) => o._wrapperFrame)
            if (found) {
                target._wrapperFrame = found._wrapperFrame

                return true
            }
        }
    }

    /**
     *
     * @param {fabric.Group} group
     * @param {fabric.Point} centerPoint
     * @param {string} originX
     * @param {string} originY
     * @param {boolean} reduce
     */
    function setGroupedPointByOriginRecursively(
        group,
        centerPoint,
        originX,
        originY,
        reduce = false
    ) {
        if (reduce) {
            centerPoint.x -= group.getPointByOrigin("center", "center").x
            centerPoint.y -= group.getPointByOrigin("center", "center").y
        } else {
            centerPoint.x += group.getPointByOrigin("center", "center").x
            centerPoint.y += group.getPointByOrigin("center", "center").y
        }

        if (group.group) {
            setGroupedPointByOriginRecursively(
                group.group,
                centerPoint,
                originX,
                originY,
                reduce
            )
        }
    }

    // TODO: temporarily disabled, caused locking of items while resizing
    //canvas.on("object:resizing", calculateAlignmentGuides);
    canvas.on("object:moving", calculateAlignmentGuides)

    function calculateAlignmentGuides({
                                          target,
                                          transform,
                                          e: { shiftKey, ctrlKey },
                                      }) {
        if (!transform) return

        var snappedWidth = false
        var snappedPosition = false
        viewportTransform = canvas.viewportTransform

        if (ctrlKey) {
            verticalLines.length = horizontalLines.length = 0
            movementLock = null

            return canvas.requestRenderAll()
        }

        if (!target) return
        // if (target.group) return;
        // if (!target.isOnScreen()) return;

        var objectsToCompare = []

        const centerPoint = target.getCenterPoint()

        const anchorX = transform.originX
        const anchorY = transform.originY

        const anchorPoint = target.translateToOriginPoint(
            centerPoint,
            anchorX,
            anchorY
        )

        //@todo: uncomment to add movement lock
        if (!target.group && shiftKey && transform.action === "drag") {
            let x2 = transform.original.left
            if (target.originX === "left")
                x2 = transform.original.left + target.width / 2
            let y2 = transform.original.top
            if (target.originY === "top")
                y2 = transform.original.top + target.height / 2

            movementLock = [
                target.__snappedTo && typeof target.__snappedTo.x === "number"
                    ? target.__snappedTo.x
                    : target.left,
                target.__snappedTo && typeof target.__snappedTo.y === "number"
                    ? target.__snappedTo.y
                    : target.top,
                x2,
                y2,
            ]
        } /**/

        if (targetIsFrameChild(target)) {
            target._wrapperFrame.children
                .filter(
                    (obj) =>
                        !obj.group &&
                        obj.isOnScreen() &&
                        obj !== target &&
                        obj.visible &&
                        obj.evented
                )
                .forEach((child) => objectsToCompare.push(child))

            objectsToCompare.push(target._wrapperFrame)
        } else {
            objectsToCompare = canvas
                .getObjects()
                .filter(
                    (obj) =>
                        !obj.group &&
                        ((target._objects && !target._objects.includes(obj)) ||
                            (target.children && !target.children.includes(obj)) ||
                            (!target.children && !target._objects)) &&
                        obj.isOnScreen() &&
                        obj !== target &&
                        obj.visible &&
                        obj.evented &&
                        !obj._wrapperFrame
                )
        }

        if (target.group) {
            const groupObjects = deepFindGroupedObjects(findTopMostGroup(target))
            objectsToCompare.push(
                ...(groupObjects.filter((go) => go._id !== target._id) || [])
            )
        }

        var targetCenter = target.getPointByOrigin("center", "center")
        if (target.group) {
            setGroupedPointByOriginRecursively(
                target.group,
                targetCenter,
                "center",
                "center"
            )
        }

        const targetBoundingRect = target.getBoundingRect(true)
        const targetWidth = targetBoundingRect.width
        const targetHeight = targetBoundingRect.height
        const targetLeft = targetCenter.x - targetWidth / 2
        const targetRight = targetCenter.x + targetWidth / 2
        const targetTop = targetCenter.y - targetHeight / 2
        const targetBottom = targetCenter.y + targetHeight / 2

        var horizontalInTheRange = false
        var verticalInTheRange = false

        const edge = {}
        const sizeEdge = {}

        // Snap to frame grid lines
        if (
            transform.action === "drag" &&
            target._wrapperFrame &&
            target._wrapperFrame.grid
        ) {
            const { width, left, height, top, grid } = target._wrapperFrame
            const { size, visible, gridMode } = grid

            if (visible && gridMode.value === "square") {
                for (let i = left; i < width + left; i += size) {
                    if (isInRange(i, targetLeft)) {
                        snappedPosition = true
                        edge.x = i + targetWidth / 2
                    }

                    if (isInRange(i, targetRight)) {
                        snappedPosition = true
                        edge.x = i - targetWidth / 2
                    }
                }

                for (let i = top; i < height + top; i += size) {
                    if (isInRange(i, targetTop)) {
                        snappedPosition = true
                        edge.y = i + targetHeight / 2
                    }

                    if (isInRange(i, targetBottom)) {
                        snappedPosition = true
                        edge.y = i - targetHeight / 2
                    }
                }
            }
        }

        objectsToCompare.forEach((object) => {
            let verticalLinesLength=verticalLines.length
            let horizontalLinesLength=horizontalLines.length
            var objectCenter = object.getPointByOrigin("center", "center")
            if (object.group) {
                setGroupedPointByOriginRecursively(
                    object.group,
                    objectCenter,
                    "center",
                    "center"
                )
            }

            const objectBoundingRect = object.getBoundingRect(true)
            const objectWidth = objectBoundingRect.width
            const objectHeight = objectBoundingRect.height
            const objectLeft = objectCenter.x - objectWidth / 2
            const objectRight = objectCenter.x + objectWidth / 2
            const objectTop = objectCenter.y - objectHeight / 2
            const objectBottom = objectCenter.y + objectHeight / 2

            //========================================================
            // snap by the left edge
            if (isInRange(objectLeft, targetLeft)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectLeft + targetWidth / 2
                } else {
                    if (
                        transform.corner === "ml" ||
                        transform.corner === "tl" ||
                        transform.corner === "bl"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectLeft
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push({
                        x: objectLeft,
                        y1: objectCenter.y < targetCenter.y ? objectBottom : objectTop,
                        y2: targetCenter.y > objectCenter.y ? y - h : y + h,
                    })
                }
            }

            // snap by the top edge
            if (isInRange(objectTop, targetTop)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectTop + targetHeight / 2
                } else {
                    if (target.angle === 0 || target.angle === 360) {
                        if (
                            transform.corner === "mt" ||
                            transform.corner === "tr" ||
                            transform.corner === "tl"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectTop
                        }
                    } else if (target.angle === 90) {
                        if (
                            transform.corner === "ml" ||
                            transform.corner === "tl" ||
                            transform.corner === "bl"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectTop
                        }
                    } else if (target.angle === 180) {
                        if (
                            transform.corner === "mb" ||
                            transform.corner === "bl" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectTop
                        }
                    } else if (target.angle === 270) {
                        if (
                            transform.corner === "mr" ||
                            transform.corner === "tr" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectTop
                        }
                    }
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push({
                        y: objectTop,
                        x1: objectCenter.x < targetCenter.x ? objectRight : objectLeft,
                        x2: targetCenter.x > objectCenter.x ? targetLeft : targetRight,
                    })
                }
            }

            //========================================================
            // snap by the right edges
            if (isInRange(objectRight, targetRight)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectRight - targetWidth / 2
                } else {
                    if (
                        transform.corner === "mr" ||
                        transform.corner === "tr" ||
                        transform.corner === "br"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectRight
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push({
                        x: objectRight,
                        y1: objectCenter.y < targetCenter.y ? objectBottom : objectTop,
                        y2: targetCenter.y > objectCenter.y ? y - h : y + h,
                    })
                }
            }

            if (isInRange(objectRight, targetLeft)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectRight + targetWidth / 2
                } else {
                    if (
                        transform.corner === "ml" ||
                        transform.corner === "tl" ||
                        transform.corner === "bl"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectRight
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push({
                        x: objectRight,
                        y1: targetCenter.y > objectCenter.y ? y - h : y + h,
                        y2: targetCenter.y > objectCenter.y ? objectBottom : objectTop,
                    })
                }
            }

            if (isInRange(objectLeft, targetRight)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectLeft - targetWidth / 2
                } else {
                    if (
                        transform.corner === "mr" ||
                        transform.corner === "tr" ||
                        transform.corner === "br"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectLeft
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push({
                        x: objectLeft,
                        y1: objectCenter.y < targetCenter.y ? objectBottom : objectTop,
                        y2: targetCenter.y > objectCenter.y ? y - h : y + h,
                    })
                }
            }

            // snap by the bottom edges
            if (isInRange(objectBottom, targetBottom)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectBottom - targetHeight / 2
                } else {
                    if (
                        transform.corner === "mb" ||
                        transform.corner === "br" ||
                        transform.corner === "bl"
                    ) {
                        snappedWidth = true
                        sizeEdge.y = objectBottom
                    }
                }

                if (target.__snappedTo) {
                    const x = target.__snappedTo.x
                    const w = targetWidth / 2

                    horizontalInTheRange = true
                    horizontalLines.push({
                        y: objectBottom,
                        x1: objectCenter.x < targetCenter.x ? objectRight : objectLeft,
                        x2: targetCenter.x > objectCenter.x ? x - w : x + w,
                    })
                }
            }

            if (isInRange(objectBottom, targetTop)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectBottom + targetHeight / 2
                } else {
                    if (target.angle === 0 || target.angle === 360) {
                        if (
                            transform.corner === "mt" ||
                            transform.corner === "tr" ||
                            transform.corner === "tl"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectBottom
                        }
                    } else if (target.angle === 90) {
                        if (
                            transform.corner === "ml" ||
                            transform.corner === "tl" ||
                            transform.corner === "bl"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectBottom
                        }
                    } else if (target.angle === 180) {
                        if (
                            transform.corner === "mb" ||
                            transform.corner === "bl" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectBottom
                        }
                    } else if (target.angle === 270) {
                        if (
                            transform.corner === "mr" ||
                            transform.corner === "tr" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectBottom
                        }
                    }
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push({
                        y: objectBottom,
                        x1: objectCenter.x < targetCenter.x ? objectRight : objectLeft,
                        x2: targetCenter.x > objectCenter.x ? targetLeft : targetRight,
                    })
                }
            }

            if (isInRange(objectTop, targetBottom)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectTop - targetHeight / 2
                } else {
                    if (target.angle === 0 || target.angle === 360) {
                        if (
                            transform.corner === "mb" ||
                            transform.corner === "br" ||
                            transform.corner === "bl"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectTop
                        }
                    } else if (target.angle === 90) {
                        if (
                            transform.corner === "mr" ||
                            transform.corner === "tr" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectTop
                        }
                    } else if (target.angle === 180) {
                        if (
                            transform.corner === "mt" ||
                            transform.corner === "tl" ||
                            transform.corner === "tr"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectTop
                        }
                    } else if (target.angle === 270) {
                        if (
                            transform.corner === "ml" ||
                            transform.corner === "tl" ||
                            transform.corner === "bl"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectTop
                        }
                    }
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push({
                        y: objectTop,
                        x1: objectCenter.x < targetCenter.x ? objectRight : objectLeft,
                        x2: targetCenter.x > objectCenter.x ? targetLeft : targetRight,
                    })
                }
            }

            // Snap edges to centers
            if (isInRange(objectCenter.x, targetRight)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectCenter.x - targetWidth / 2
                } else {
                    if (
                        transform.corner === "mr" ||
                        transform.corner === "tr" ||
                        transform.corner === "br"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectCenter.x
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push(
                        {
                            x: objectCenter.x,
                            y1: targetCenter.y < objectCenter.y ? y - h : y + h,
                            y2: objectBottom,
                        },
                        {
                            x: objectCenter.x,
                            y1: objectBottom,
                            y2: objectTop,
                        }
                    )
                }
            }

            if (isInRange(objectCenter.x, targetLeft)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectCenter.x + targetWidth / 2
                } else {
                    if (
                        transform.corner === "ml" ||
                        transform.corner === "tl" ||
                        transform.corner === "bl"
                    ) {
                        snappedWidth = true
                        sizeEdge.x = objectCenter.x
                    }
                }

                if (target.__snappedTo) {
                    const y = target.__snappedTo.y
                    const h = targetHeight / 2

                    verticalInTheRange = true
                    verticalLines.push(
                        {
                            x: objectCenter.x,
                            y1: targetCenter.y < objectCenter.y ? y - h : y + h,
                            y2: objectBottom,
                        },
                        {
                            x: objectCenter.x,
                            y1: objectBottom,
                            y2: objectTop,
                        }
                    )
                }
            }

            if (isInRange(objectCenter.y, targetTop)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectCenter.y + targetHeight / 2
                } else {
                    if (target.angle === 0 || target.angle === 360) {
                        if (
                            transform.corner === "mt" ||
                            transform.corner === "tl" ||
                            transform.corner === "tr"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectCenter.y
                        }
                    } else if (target.angle === 90) {
                        if (
                            transform.corner === "ml" ||
                            transform.corner === "tl" ||
                            transform.corner === "bl"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectCenter.y
                        }
                    } else if (target.angle === 180) {
                        if (
                            transform.corner === "mb" ||
                            transform.corner === "bl" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.y = objectCenter.y
                        }
                    } else if (target.angle === 270) {
                        if (
                            transform.corner === "mr" ||
                            transform.corner === "tr" ||
                            transform.corner === "br"
                        ) {
                            snappedWidth = true
                            sizeEdge.x = objectCenter.y
                        }
                    }
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push(
                        {
                            y: objectCenter.y,
                            x1: targetCenter.x < objectCenter.x ? targetRight : targetLeft,
                            x2: objectLeft,
                        },
                        {
                            y: objectCenter.y,
                            x1: objectLeft,
                            x2: objectRight,
                        }
                    )
                }
            }

            if (isInRange(objectCenter.y, targetBottom)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectCenter.y - targetHeight / 2
                } else {
                    if (
                        transform.corner === "mb" ||
                        transform.corner === "bl" ||
                        transform.corner === "br"
                    ) {
                        snappedWidth = true
                        sizeEdge.y = objectCenter.y
                    }
                }

                if (target.__snappedTo) {
                    const x = target.__snappedTo.x
                    const w = targetWidth / 2

                    horizontalInTheRange = true
                    horizontalLines.push(
                        {
                            y: objectCenter.y,
                            x1: targetCenter.x < objectCenter.x ? x - w : x + w,
                            x2: objectLeft,
                        },
                        {
                            y: objectCenter.y,
                            x1: objectLeft,
                            x2: objectRight,
                        }
                    )
                }
            }

            //Canvas center snap
            if (isInRange(targetCenter.y, (canvas.height/2)/canvas.getZoom())) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = (canvas.height/2)/canvas.getZoom()
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push({
                        y: (canvas.height/2)/canvas.getZoom(),
                        x1: 0,
                        x2: (canvas.width/canvas.getZoom())
                    })
                }
            }
            //Canvas center snap
            if (isInRange(targetCenter.x, (canvas.width/2)/canvas.getZoom())) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = (canvas.width/2)/canvas.getZoom()
                }
                if (target.__snappedTo) {
                    verticalInTheRange = true;
                    verticalLines.push({
                        x: (canvas.width/2)/canvas.getZoom(),
                        y1: 0,
                        y2: (canvas.height/canvas.getZoom()),
                    });
                }
            }

            // snap by the centers
            if (isInRange(objectCenter.x, targetCenter.x)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectCenter.x
                }

                if (target.__snappedTo) {
                    verticalInTheRange = true
                    verticalLines.push(
                        {
                            x: objectCenter.x,
                            y1: target.__snappedTo.y,
                            y2: objectTop,
                        },
                        {
                            x: objectCenter.x,
                            y1: target.__snappedTo.y,
                            y2: objectBottom,
                        }
                    )
                }
            }

            if (isInRange(objectLeft, targetCenter.x)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectLeft
                }

                if (target.__snappedTo) {
                    verticalInTheRange = true
                    verticalLines.push(
                        {
                            x: objectLeft,
                            y1: target.__snappedTo.y,
                            y2: objectTop,
                        },
                        {
                            x: objectLeft,
                            y1: target.__snappedTo.y,
                            y2: objectBottom,
                        }
                    )
                }
            }

            if (isInRange(objectRight, targetCenter.x)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.x = objectRight
                }

                if (target.__snappedTo) {
                    verticalInTheRange = true
                    verticalLines.push(
                        {
                            x: objectRight,
                            y1: target.__snappedTo.y,
                            y2: objectTop,
                        },
                        {
                            x: objectRight,
                            y1: target.__snappedTo.y,
                            y2: objectBottom,
                        }
                    )
                }
            }

            if (isInRange(objectCenter.y, targetCenter.y)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectCenter.y
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push(
                        {
                            y: objectCenter.y,
                            x1: target.__snappedTo.x,
                            x2: objectLeft,
                        },
                        {
                            y: objectCenter.y,
                            x1: target.__snappedTo.x,
                            x2: objectRight,
                        }
                    )
                }
            }

            if (isInRange(objectBottom, targetCenter.y)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectBottom
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push(
                        {
                            y: objectBottom,
                            x1: target.__snappedTo.x,
                            x2: objectLeft,
                        },
                        {
                            y: objectBottom,
                            x1: target.__snappedTo.x,
                            x2: objectRight,
                        }
                    )
                }
            }

            if (isInRange(objectTop, targetCenter.y)) {
                if (transform.action === "drag") {
                    snappedPosition = true
                    edge.y = objectTop
                }

                if (target.__snappedTo) {
                    horizontalInTheRange = true
                    horizontalLines.push(
                        {
                            y: objectTop,
                            x1: target.__snappedTo.x,
                            x2: objectLeft,
                        },
                        {
                            y: objectTop,
                            x1: target.__snappedTo.x,
                            x2: objectRight,
                        }
                    )
                }
            }
            if(object.type==='guideLine')
            {
                verticalLines.length=verticalLinesLength;
                horizontalLines.length=horizontalLinesLength;
            }
        })

        if (!horizontalInTheRange) {
            horizontalLines.length = 0
            delete target.__snappedTo
        }

        if (!verticalInTheRange) {
            verticalLines.length = 0
            delete target.__snappedTo
        }

        if (transform.action === "drag") {
            if (snappedPosition) {
                // create snap point
                const point = new fabric.Point(
                    typeof edge.x === "number" ? edge.x : targetCenter.x,
                    typeof edge.y === "number" ? edge.y : targetCenter.y
                )

                const position = { ...point }

                if (target.group) {
                    setGroupedPointByOriginRecursively(
                        target.group,
                        position,
                        "center",
                        "center",
                        true
                    )
                }

                // snap to point on move
                target.setPositionByOrigin(
                    {
                        x: position.x,
                        y: position.y,
                    },
                    "center",
                    "center"
                )

                target.__snappedTo = point
                target.setCoords()

                canvas.fire("object:snapped", {
                    target,
                })
            }
        } else {
            if (snappedWidth) {
                // create snap point
                const point = new fabric.Point(
                    typeof sizeEdge.x === "number" ? sizeEdge.x : targetCenter.x,
                    typeof sizeEdge.y === "number" ? sizeEdge.y : targetCenter.y
                )
                target.__snappedTo = point

                if (typeof sizeEdge.x === "number") {
                    if (
                        transform.corner === "ml" ||
                        transform.corner === "tl" ||
                        transform.corner === "bl"
                    ) {
                        // if angle === 0
                        var width =
                            sizeEdge.x - (targetBoundingRect.left + targetBoundingRect.width)

                        if (target.angle === 90) {
                            width =
                                sizeEdge.x -
                                (targetBoundingRect.top + targetBoundingRect.height)
                        }
                    } else if (
                        transform.corner === "mr" ||
                        transform.corner === "tr" ||
                        transform.corner === "br"
                    ) {
                        width = sizeEdge.x - targetBoundingRect.left

                        if (target.angle === 270) {
                            width =
                                sizeEdge.x -
                                (targetBoundingRect.top + targetBoundingRect.height)
                        }

                        if (target.angle === 90) {
                            // TODO: fix from here...
                            width =
                                sizeEdge.x -
                                (targetBoundingRect.left + targetBoundingRect.width)
                        }
                    }

                    target.set("width", Math.abs(width))
                }

                if (typeof sizeEdge.y === "number") {
                    if (
                        transform.corner === "mt" ||
                        transform.corner === "tl" ||
                        transform.corner === "tr"
                    ) {
                        var height =
                            sizeEdge.y - (targetBoundingRect.top + targetBoundingRect.height)
                    } else if (
                        transform.corner === "mb" ||
                        transform.corner === "bl" ||
                        transform.corner === "br"
                    ) {
                        height = sizeEdge.y - targetBoundingRect.top

                        if (target.angle === 180) {
                            height =
                                sizeEdge.y -
                                (targetBoundingRect.top + targetBoundingRect.height)
                        }
                    }

                    target.set("height", Math.abs(height))
                }

                target.setPositionByOrigin(anchorPoint, anchorX, anchorY)
                target.setCoords()
            }
        }
    }

    canvas.on("object:rotating", function ({ target, pointer }) {
        if (target.type == "LineAnnotateArrow") return
        numberGuide = {
            number: Math.round(target.angle) + "ᵒ",
            x: pointer.x,
            y: pointer.y,
            minWidth: 21,
            offsetX: 25,
            offsetY: 15,
        }
    })

    canvas.on("object:pattern", function ({ target, pointer }) {
        if (!target.fill || !(target.fill instanceof fabric.Pattern)) return

        const angle = resetObjectAngle({
            angle: Math.round(target.fill.rotation),
        })
        const scale = Math.round(target.fill.scaling * 100)

        dualNumbersGuide = {
            numbers: [
                {
                    label: "Rotation",
                    value: angle + "ᵒ",
                },
                {
                    label: "Scale",
                    value: scale + "%",
                },
            ],
            x: pointer.x,
            y: pointer.y,
            minWidth: 91,
            height: 32,
            offsetX: -40,
            offsetY: 10,
        }
    })

    canvas.on("object:font:size", function ({ target, pointer }) {
        numberGuide = {
            number: target.fontSize + "px",
            offsetX: 25,
            offsetY: 15,
            x: pointer.x,
            y: pointer.y,
        }
    })

    canvas.on("object:shape:sides", function ({ target, pointer }) {
        numberGuide = {
            number: "Sides " + target.sides,
            offsetX: 30,
            offsetY: 15,
            x: pointer.x,
            y: pointer.y,
        }
    })

    function getCornerRadiusIndexByCorner(corner) {
        if (corner === "rtl") return 0
        if (corner === "rtr") return 1
        if (corner === "rbr") return 2
        if (corner === "rbl") return 3
    }

    canvas.on("object:corner:radius", function ({ target, pointer }) {
        if (Array.isArray(target.cornerRadius)) {
            if (target.unifromRadius) {
                var number = "Radius " + target.cornerRadius[0]
            } else {
                const index = getCornerRadiusIndexByCorner(target.__corner)
                number = "Radius " + target.cornerRadius[index]
            }
            numberGuide = {
                number,
                offsetX: 37,
                offsetY: 15,
                x: pointer.x,
                y: pointer.y,
            }
        } else {
            numberGuide = {
                number: "Radius " + target.cornerRadius,
                offsetX: 37,
                offsetY: 15,
                x: pointer.x,
                y: pointer.y,
            }
        }
    })

    canvas.on("object:inner:radius", function ({ target, pointer }) {
        numberGuide = {
            number: Math.round((target.innerWidth / target.width) * 100) + "%",
            offsetX: 25,
            offsetY: 15,
            x: pointer.x,
            y: pointer.y,
        }
    })

    canvas.on("object:start:angle", function ({ target, pointer }) {
        numberGuide = {
            number: "Start " + Math.round(target.startAngle) + "ᵒ",
            offsetX: 35,
            offsetY: 17,
            x: pointer.x,
            y: pointer.y,
        }
    })

    canvas.on("object:end:angle", function ({ target, pointer }) {
        numberGuide = {
            number: "End " + Math.round(target.endAngle) + "ᵒ",
            offsetX: 35,
            offsetY: 17,
            x: pointer.x,
            y: pointer.y,
        }
    })

    // On mouse over show hover rect
    canvas.on("mouse:over", ({ target }) => {
        if(!canvas.getActiveObject()) return
        if (canvas.__isEditingText) return
        if (canvas.__pan || canvas.__panning) return
        if (canvas.penToolMode || canvas.isDrawingMode) return
        if (!target) return

        if (canvas.getActiveObject() === target) return

        if (target.isHovered || !target.evented) return

        target.isHovered = true
        const zoom = canvas.getZoom()
        const oCoords = target.hasOwnProperty('calcCoords') ? target.calcCoords(true) : undefined

        if (target.type !== "mockup") {
            handleDrawHoverGuides(target)
        }

        function handleDrawHoverGuides(obj) {
            // if (obj.type === 'group') {
            //   obj.forEachObject(o => handleDrawHoverGuides(o));
            // }
            let oCoords= obj.hasOwnProperty('calcCoords') ? obj.calcCoords(true) : undefined;
            oCoords && drawHoverGuidesForTextFrame(ctx, obj, oCoords, canvas, zoom)
        }

        if (target.group) return
        // Any other object type has a simple rectangle bound

        if(!oCoords) return

        ctx.save()
        ctx.lineWidth = 1.3 / zoom

        ctx.strokeStyle =  canvas.hoverStrokeColor
        ctx.transform(...canvas.viewportTransform)
        ctx.beginPath()

        ctx.moveTo(oCoords.tl.x, oCoords.tl.y)
        ctx.lineTo(oCoords.tr.x, oCoords.tr.y)
        ctx.lineTo(oCoords.br.x,  oCoords.br.y)
        ctx.lineTo(oCoords.bl.x, oCoords.bl.y)
        ctx.closePath()
        ctx.stroke()
        ctx.restore()
    })

    canvas.on("mouse:out", ({ target }) => {
        if (target) {
            target.isHovered = false

            if (!canvas.__isEditingText) {
                canvas.contextTop && canvas.clearContext(canvas.contextTop)
            }

            const zoom = canvas.getZoom()
            if (zoom > 8) {
                createPixelGridLines(canvas, zoom)
            }
        }
        if (canvas.showRulers) {
            ctx.save()
            ctx.restore()
            canvas.rulerGuides.forEach(line=>{
                if(canvas._activeObject&&canvas._activeObject===line){
                    return;
                }else{
                    line._customRender(ctx)
                }
            })

        }
    })

    canvas.on("zoom:end", () => {
        canvas.forEachObject((obj) => (obj.isHovered = false))
    })

    canvas.on("scroll:end", () => {
        canvas.forEachObject((obj) => (obj.isHovered = false))
    })

    canvas.on("before:render", function () {
        canvas.contextTop && canvas.clearContext(canvas.contextTop)
    })

    canvas.on("mouse:up", function () {
        verticalLines.length = horizontalLines.length = 0
        movementLock = null
        numberGuide = null
        dualNumbersGuide = null
        canvas.requestRenderAll()
    })

    /*canvas.on("text:selection:changed", function() {
          canvas.getObjects('frame').forEach(frame => {
              frame.drawGridLines();
          });
      });
      canvas.on("text:changed", function() {
          canvas.getObjects('frame').forEach(frame => {
              frame.drawGridLines();
          });
      });*/

    canvas.on("after:render", function () {
        const zoom = canvas.getZoom()

        if (zoom > 8) {
            createPixelGridLines(canvas, zoom)
        }
        if (canvas._activeObject?.type==='guideLine'){
            movementLock = null
            horizontalDistance = null
            verticalDistance = null
            widthDistance = null
            heightDistance = null
            verticalLines.length = horizontalLines.length = 0
        }

        verticalLines.slice(0, 4).forEach((line) => {
            drawVerticalLines(line)
        })
        horizontalLines.slice(0, 4).forEach((line) => {
            drawHorizontalLines(line)
        })

        // if (dualNumbersGuide) drawDualNumbersGuide(dualNumbersGuide)
        // if (numberGuide) drawNumberGuide(numberGuide)
        // if (movementLock) drawMovementLockLine(movementLock)

        if (horizontalDistance) drawHorizontalDistanceLines(horizontalDistance)
        if (widthDistance) drawHorizontalDistanceLines(widthDistance)

        if (verticalDistance) drawVerticalDistanceLines(verticalDistance)
        if (heightDistance) drawVerticalDistanceLines(heightDistance)

        if (horizontalDistance) drawHorizontalDistance(horizontalDistance)
        if (widthDistance) drawHorizontalDistance(widthDistance)

        if (verticalDistance) drawVerticalDistance(verticalDistance)
        if (heightDistance) drawVerticalDistance(heightDistance)

        movementLock = null
        horizontalDistance = null
        verticalDistance = null
        widthDistance = null
        heightDistance = null
        verticalLines.length = horizontalLines.length = 0
        /*if (canvas.showRulers) {
            ctx.save()
            drawRulers(ctx, canvas, canvas.getZoom(), "light")
            ctx.restore()
            canvas.rulerGuides.forEach(line=>{
                if(canvas._activeObject&&canvas._activeObject===line){
                    return;
                }else{
                    line._customRender(ctx)
                }
            })
        }*/
    })
}
